import { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from "geojson";
import { LngLatBounds } from "maplibre-gl";
import type { Job } from "../../models";
import type { ColorMode } from "../../components/helpers/JobMap/JobMap.types";
import { LAYER_COLORS } from "../../components/helpers/JobMap/JobMap.types";
import {
	getJobColor,
	getShapeFeatureProperty,
	parseChangeInfo,
	getJobBoundaryGeometry,
} from "../../components/helpers/JobMap/JobMap.helpers";
import { normalizePointsFC } from "../../domain/sample/sampleService";
import {
	BOUNDARY_COLORS,
	NEARBY_JOB_COLOR,
	HATCH_ANGLES,
	HATCH_LINE_SPACING_METERS,
	UNMATCHED_POINT_COLOR,
	UNMATCHED_ZONE_COLOR,
	SELECTED_COLOR,
	SELECTED_OPACITY,
	HIGHLIGHT_COLOR,
	HIGHLIGHT_STROKE_COLOR,
} from "../../components/SubmitJobs/Methods/Bulk/wizard/shared/constants";
import type { NearbyJobInfo } from "../../components/SubmitJobs/Methods/Bulk/wizard/WizardContainer";
import type { MapFeatureData } from "../../components/SubmitJobs/Methods/Bulk/wizard/steps/OverlappingPoints";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProcessedGeoData {
	polygonFeatures: Feature<Polygon>[];
	pointFeatures: Feature<Point>[];
	bounds: LngLatBounds | undefined;
	jobLookup: Map<string, Job>;
}

export interface BoundaryModeInput {
	jobs: Job[];
	colorMode: "boundary-hatch" | "boundary-outline";
	hiddenJobIds?: string[];
	nearbyJobs?: NearbyJobInfo[];
	hiddenNearbyJobIds?: string[];
	unmatchedFeatures?: FeatureCollection;
	hideUnmatchedFeatures?: boolean;
	duplicateFeatures?: MapFeatureData[];
	hiddenDuplicateFeatureIds?: Set<string>;
	highlightGeometry?: Feature;
	selectedFeatureIds?: Set<string>;
}

export interface BoundaryModeResult {
	polygonFeatures: Feature<Polygon>[];
	nearbyJobFeatures: Feature<Polygon>[];
	unmatchedPointFeatures: Feature<Point>[];
	unmatchedZoneFeatures: Feature<Polygon>[];
	duplicatePointFeatures: Feature<Point>[];
	duplicateZoneFeatures: Feature<Polygon>[];
	highlightFeatures: Feature<Polygon | MultiPolygon>[];
	bounds: LngLatBounds | undefined;
	hatchInputs: Array<{ geometry: Polygon | MultiPolygon; angle: number; spacing: number; color: string }>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function extendBoundsFromGeometry(bounds: LngLatBounds, geom: Polygon | MultiPolygon) {
	const coordSets = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
	for (const polygon of coordSets) {
		for (const ring of polygon) {
			for (const coord of ring) {
				bounds.extend([coord[0], coord[1]]);
			}
		}
	}
}

export function geometryToPolygonFeatures(geom: Polygon | MultiPolygon): Polygon[] {
	if (geom.type === "Polygon") return [geom];
	return geom.coordinates.map((coords) => ({ type: "Polygon" as const, coordinates: coords }));
}

// ── Standard mode processing ─────────────────────────────────────────────────

export function processStandardJobs(
	jobs: Job[],
	colorMode: ColorMode,
	selectedFeatureIds?: Set<string>,
): ProcessedGeoData {
	const polygonFeatures: Feature<Polygon>[] = [];
	const pointFeatures: Feature<Point>[] = [];
	const jobLookup = new Map<string, Job>();
	let bounds: LngLatBounds | undefined;

	function extendBounds(lng: number, lat: number) {
		if (!bounds) bounds = new LngLatBounds([lng, lat], [lng, lat]);
		else bounds.extend([lng, lat]);
	}

	function processPolygonFC(fc: FeatureCollection, job: Job, jobId: string, typePrefix: string) {
		fc.features?.forEach((feature, featureIndex) => {
			if (!feature || (feature.geometry.type !== "Polygon" && feature.geometry.type !== "MultiPolygon")) return;
			const geom = feature.geometry as Polygon | MultiPolygon;

			// Extend bounds
			const coordSets = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
			for (const polygon of coordSets) {
				for (const ring of polygon) {
					for (const coord of ring) {
						extendBounds(coord[0], coord[1]);
					}
				}
			}

			// Split into per-path Polygon features
			coordSets.forEach((polyCoords, pathIndex) => {
				const polygonId = `${typePrefix}-${jobId}-${featureIndex}-${pathIndex}`;
				const isSelected = selectedFeatureIds?.has(polygonId);

				const layerColor =
					typePrefix === "boundary"
						? LAYER_COLORS.boundary
						: typePrefix === "zones"
							? LAYER_COLORS.zones
							: LAYER_COLORS.points;
				const baseColor = colorMode === "layer-type" ? layerColor : getJobColor(jobId);

				polygonFeatures.push({
					type: "Feature",
					properties: {
						id: polygonId,
						jobId,
						color: isSelected ? SELECTED_COLOR : baseColor,
						opacity: isSelected ? 0.6 : 0.4,
						strokeColor: isSelected ? SELECTED_COLOR : baseColor,
						fieldName: job.importFieldName || job.fieldName || "Unnamed Field",
						farmName: job.importFarmName || job.farmName || "",
						growerName: job.importGrowerName || job.growerName || "",
					},
					geometry: { type: "Polygon", coordinates: polyCoords },
				});
			});
		});

		jobLookup.set(jobId, job);
	}

	function processPointFC(fc: FeatureCollection, jobId: string) {
		// Defense-in-depth: ensure canonical sample_id even if caller didn't normalize
		const normalized = normalizePointsFC(fc);
		normalized.features?.forEach((feature, featureIndex) => {
			const geom = feature.geometry;
			const [lng, lat] = geom.coordinates;
			extendBounds(lng, lat);

			const sampleId = feature.properties.sample_id;
			const barcode = getShapeFeatureProperty(feature, "sample_barcode");
			const change = getShapeFeatureProperty(feature, "change");
			const { hasChange, changeType, changeReason } = parseChangeInfo(change);

			const pointId = `points-${jobId}-${featureIndex}`;
			const isSelected = selectedFeatureIds?.has(pointId);

			const color = isSelected ? SELECTED_COLOR : LAYER_COLORS.points;

			pointFeatures.push({
				type: "Feature",
				properties: {
					id: pointId,
					jobId,
					sampleId,
					barcode,
					hasChange,
					changeType,
					changeReason,
					isSelected: !!isSelected,
					color,
					label: sampleId || "",
				},
				geometry: geom,
			});
		});
	}

	jobs.forEach((job) => {
		const jobId = job.id || "unknown";
		jobLookup.set(jobId, job);

		const boundary = job.files?.boundaryGeoJSON as FeatureCollection | undefined;
		if (boundary) processPolygonFC(boundary, job, jobId, "boundary");

		const zones = job.files?.zonesGeoJSON as FeatureCollection | undefined;
		if (zones) processPolygonFC(zones, job, jobId, "zones");

		const points = job.files?.pointsGeoJSON as FeatureCollection | undefined;
		if (points) processPointFC(points, jobId);
	});

	return { polygonFeatures, pointFeatures, bounds, jobLookup };
}

// ── Boundary/wizard mode processing (sync) ───────────────────────────────────

export function processBoundarySync(input: BoundaryModeInput): BoundaryModeResult {
	const {
		jobs,
		colorMode,
		hiddenJobIds,
		nearbyJobs,
		hiddenNearbyJobIds,
		unmatchedFeatures,
		hideUnmatchedFeatures,
		duplicateFeatures,
		hiddenDuplicateFeatureIds,
		highlightGeometry,
		selectedFeatureIds,
	} = input;

	const polygonFeatures: Feature<Polygon>[] = [];
	const nearbyJobFeatures: Feature<Polygon>[] = [];
	const unmatchedPointFeatures: Feature<Point>[] = [];
	const unmatchedZoneFeatures: Feature<Polygon>[] = [];
	const duplicatePointFeatures: Feature<Point>[] = [];
	const duplicateZoneFeatures: Feature<Polygon>[] = [];
	const highlightFeatures: Feature<Polygon | MultiPolygon>[] = [];
	const hatchInputs: BoundaryModeResult["hatchInputs"] = [];
	let bounds: LngLatBounds | undefined;

	function ensureBounds(lng: number, lat: number) {
		if (!bounds) bounds = new LngLatBounds([lng, lat], [lng, lat]);
		else bounds.extend([lng, lat]);
	}

	const isDuplicateFeaturesMode = colorMode === "boundary-outline";

	// 1. Main job boundaries
	jobs.forEach((job, index) => {
		const isHidden = hiddenJobIds?.includes(job.id);
		const boundaryGeom = getJobBoundaryGeometry(job);
		if (!boundaryGeom) return;

		extendBoundsFromGeometry(
			bounds ??
				(bounds = new LngLatBounds(
					[
						boundaryGeom.type === "Polygon" ? boundaryGeom.coordinates[0][0][0] : boundaryGeom.coordinates[0][0][0][0],
						boundaryGeom.type === "Polygon" ? boundaryGeom.coordinates[0][0][1] : boundaryGeom.coordinates[0][0][0][1],
					],
					[
						boundaryGeom.type === "Polygon" ? boundaryGeom.coordinates[0][0][0] : boundaryGeom.coordinates[0][0][0][0],
						boundaryGeom.type === "Polygon" ? boundaryGeom.coordinates[0][0][1] : boundaryGeom.coordinates[0][0][0][1],
					],
				)),
			boundaryGeom,
		);

		if (isHidden) return;

		const color = BOUNDARY_COLORS[index % BOUNDARY_COLORS.length];
		const polys = geometryToPolygonFeatures(boundaryGeom);

		polys.forEach((polyGeom, pathIndex) => {
			polygonFeatures.push({
				type: "Feature",
				properties: {
					id: isDuplicateFeaturesMode ? `outline-${job.id}-${pathIndex}` : `job-${job.id}-${pathIndex}`,
					jobId: job.id,
					color,
					fillOpacity: isDuplicateFeaturesMode ? 0 : 0.4,
					strokeColor: color,
					strokeWeight: 3,
					strokeOpacity: 1,
				},
				geometry: polyGeom,
			});
		});

		// Hatch line input
		const angle = HATCH_ANGLES[index % HATCH_ANGLES.length];
		hatchInputs.push({ geometry: boundaryGeom, angle, spacing: HATCH_LINE_SPACING_METERS, color });
	});

	// 2. Nearby job boundaries
	nearbyJobs?.forEach((nearby) => {
		const isHidden = hiddenNearbyJobIds?.includes(nearby.job.id);
		const boundaryGeom = getJobBoundaryGeometry(nearby.job);
		if (!boundaryGeom) return;

		// Always extend bounds
		if (!bounds) {
			const firstCoord =
				boundaryGeom.type === "Polygon" ? boundaryGeom.coordinates[0][0] : boundaryGeom.coordinates[0][0][0];
			bounds = new LngLatBounds([firstCoord[0], firstCoord[1]], [firstCoord[0], firstCoord[1]]);
		}
		extendBoundsFromGeometry(bounds, boundaryGeom);

		if (isHidden) return;

		const polys = geometryToPolygonFeatures(boundaryGeom);
		polys.forEach((polyGeom, pathIndex) => {
			nearbyJobFeatures.push({
				type: "Feature",
				properties: {
					id: `nearby-${nearby.job.id}-${pathIndex}`,
					jobId: nearby.job.id,
					color: NEARBY_JOB_COLOR,
					fillOpacity: 0.2,
					strokeColor: NEARBY_JOB_COLOR,
					strokeWeight: 2,
					strokeOpacity: 0.5,
				},
				geometry: polyGeom,
			});
		});
	});

	// 3. Unmatched features
	if (unmatchedFeatures?.features && !hideUnmatchedFeatures) {
		unmatchedFeatures.features.forEach((feature, index) => {
			const geom = feature.geometry;
			if (!geom) return;

			if (geom.type === "Point") {
				const point = geom as Point;
				ensureBounds(point.coordinates[0], point.coordinates[1]);
				const featureId = `unmatched-point-${index}`;
				const isSelected = selectedFeatureIds?.has(featureId);
				unmatchedPointFeatures.push({
					type: "Feature",
					properties: {
						id: featureId,
						color: isSelected ? SELECTED_COLOR : UNMATCHED_POINT_COLOR,
						size: isSelected ? 16 : 14,
						strokeColor: isSelected ? "#fff" : "#666",
						strokeWidth: isSelected ? 3 : 2,
						isSelected: !!isSelected,
					},
					geometry: point,
				});
			} else if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
				const polyGeom = geom as Polygon | MultiPolygon;
				if (!bounds) {
					const firstCoord = polyGeom.type === "Polygon" ? polyGeom.coordinates[0][0] : polyGeom.coordinates[0][0][0];
					bounds = new LngLatBounds([firstCoord[0], firstCoord[1]], [firstCoord[0], firstCoord[1]]);
				}
				extendBoundsFromGeometry(bounds, polyGeom);

				const polys = geometryToPolygonFeatures(polyGeom);
				polys.forEach((poly, pathIndex) => {
					const featureId = `unmatched-zone-${index}-${pathIndex}`;
					const isSelected = selectedFeatureIds?.has(featureId);
					unmatchedZoneFeatures.push({
						type: "Feature",
						properties: {
							id: featureId,
							color: isSelected ? SELECTED_COLOR : UNMATCHED_ZONE_COLOR,
							fillOpacity: isSelected ? SELECTED_OPACITY : 0.4,
							strokeColor: isSelected ? SELECTED_COLOR : UNMATCHED_ZONE_COLOR,
							strokeWeight: isSelected ? 4 : 3,
							isSelected: !!isSelected,
						},
						geometry: poly,
					});
				});
			}
		});
	}

	// 4. Duplicate features
	if (duplicateFeatures) {
		const sorted = [...duplicateFeatures].sort((a, b) => (b.size ?? 14) - (a.size ?? 14));
		sorted.forEach((featureData) => {
			const { feature, color, size, groupId, featureIndex } = featureData;
			const featureId = `${groupId}-${featureIndex}`;
			if (hiddenDuplicateFeatureIds?.has(featureId)) return;

			const geom = feature.geometry;
			if (!geom) return;

			if (geom.type === "Point") {
				const point = geom as Point;
				ensureBounds(point.coordinates[0], point.coordinates[1]);
				duplicatePointFeatures.push({
					type: "Feature",
					properties: {
						id: `duplicate-${featureId}`,
						color,
						size: size || 14,
						strokeColor: "#fff",
						strokeWidth: 2,
					},
					geometry: point,
				});
			} else if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
				const polyGeom = geom as Polygon | MultiPolygon;
				if (!bounds) {
					const firstCoord = polyGeom.type === "Polygon" ? polyGeom.coordinates[0][0] : polyGeom.coordinates[0][0][0];
					bounds = new LngLatBounds([firstCoord[0], firstCoord[1]], [firstCoord[0], firstCoord[1]]);
				}
				extendBoundsFromGeometry(bounds, polyGeom);

				const polys = geometryToPolygonFeatures(polyGeom);
				polys.forEach((poly, pathIndex) => {
					duplicateZoneFeatures.push({
						type: "Feature",
						properties: {
							id: `duplicate-${featureId}-${pathIndex}`,
							color,
							fillOpacity: 0.4,
							strokeColor: color,
							strokeWeight: 3,
						},
						geometry: poly,
					});
				});
			}
		});
	}

	// 5. Highlight geometry
	if (highlightGeometry?.geometry) {
		const geom = highlightGeometry.geometry;
		if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
			const polyGeom = geom as Polygon | MultiPolygon;
			if (!bounds) {
				const firstCoord = polyGeom.type === "Polygon" ? polyGeom.coordinates[0][0] : polyGeom.coordinates[0][0][0];
				bounds = new LngLatBounds([firstCoord[0], firstCoord[1]], [firstCoord[0], firstCoord[1]]);
			}
			extendBoundsFromGeometry(bounds, polyGeom);
			highlightFeatures.push({
				type: "Feature",
				properties: {
					color: HIGHLIGHT_COLOR,
					fillOpacity: 0.3,
					strokeColor: HIGHLIGHT_STROKE_COLOR,
					strokeWeight: 2,
				},
				geometry: polyGeom,
			});
		}
	}

	return {
		polygonFeatures,
		nearbyJobFeatures,
		unmatchedPointFeatures,
		unmatchedZoneFeatures,
		duplicatePointFeatures,
		duplicateZoneFeatures,
		highlightFeatures,
		bounds,
		hatchInputs,
	};
}
